import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';

import { Button } from '../../components/ui/button';
import { GitBranch } from 'lucide-react';
// Note: Connection creation is handled by the existing GenogramConnectorTool
// This tool just triggers the connection mode

/**
 * Connect tool component for the floating panel
 */
const ConnectToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const selectedCount = context.selection.size;
  const isDisabled = selectedCount === 0;
  const [isConnecting, setIsConnecting] = React.useState(false);

  React.useEffect(() => {
    const unsubscribe = context.eventBus.on('tool:connecting-state', (payload?: { active?: boolean }) => {
      setIsConnecting(Boolean(payload?.active));
    });
    return unsubscribe;
  }, [context.eventBus]);

  const handleConnect = () => {
    if (!isDisabled) {
      onExecute({ action: isConnecting ? 'cancel_connecting' : 'start_connecting', context });
    }
  };

  return React.createElement(Button, {
    key: 'connect-button',
    variant: isConnecting ? 'secondary' : 'ghost',
    size: 'sm',
    disabled: isDisabled,
    onClick: handleConnect,
    className: 'h-8 w-8 p-0 rounded-md',
    'aria-label': isDisabled ? 'Select a shape first' : isConnecting ? 'Cancel connector' : 'Draw connector'
  }, React.createElement(GitBranch, { key: 'icon', className: 'h-4 w-4', 'aria-hidden': true }));
};

/**
 * Tool for creating connections between shapes in the contextual toolbar
 */
export class ConnectTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'connect',
    name: 'tools.connect',
    icon: 'link',
    category: 'create',
    description: 'Connect selected shape to another shape',
    version: '1.0.0',
    
    requiresSelection: true,
    minSelection: 1,
    maxSelection: 1, // Only allow single selection for connecting
    
    ui: {
      toolbar: false,
      contextMenu: true,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true, // This makes it appear in contextual toolbar
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: false,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: true,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    }
  };

  canExecute(context: IToolContext): boolean {
    // Only allow if exactly one shape is selected
    return super.canExecute(context) && context.selection.size === 1;
  }

  execute(params?: { action: string; context?: IToolContext }): ICommand | null {
    // Use context from params if available, otherwise fall back to this.context
    const context = params?.context || this.context;
    
    if (!context || context.selection.size !== 1) {
      return null;
    }

    if (params?.action === 'start_connecting') {
      // Get the selected node ID
      const selectedNodeId = Array.from(context.selection)[0];
      
      // Emit event to start connection mode
      context.eventBus.emit('tool:start-connecting', { 
        sourceNodeId: selectedNodeId 
      });
      
      return null; // No immediate command, connection will be created on second click
    }

    if (params?.action === 'cancel_connecting') {
      context.eventBus.emit('tool:cancel-connecting');
      return null;
    }

    return null;
  }

  getFloatingPanelComponent() {
    return ConnectToolComponent;
  }
}
